import { SelfContainedCommand, CommandExecutionContext } from "../../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../../types/standardToolResults";
import { createFileLogger } from "../../../utils/logger";
import { entityService } from "../../../services/entityService";
import * as Y from 'yjs';
import { PROJECT_TEMPLATES } from '../services/projectTemplates';
import { REACT_SPA_PROMPT } from './templates/react-spa';

const log = createFileLogger('APP_BUILDER_CREATE');

export const appBuilderCreateCommand: SelfContainedCommand = {
  id: 'app_builder_create',
  
  description: `Create a new app that opens in the App Builder IDE.

**CODE WRITING GUIDELINES:**
- Write standard TypeScript/React code with normal imports
- NPM packages auto-convert to ESM (just write: import X from 'package-name')
- Local imports work: import { X } from './components/X'
- All React hooks available globally

**ICON:** Pass an optional \`icon\` (Phosphor name) that reflects the app's purpose — always set this. Examples: "Calculator", "ChartBar", "Heart", "ShoppingCart", "Notepad", "GameController".

**RETURNS:** entityId and windowId (project opens in AppBuilderApp)
`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Project name (e.g., "Calculator App", "Todo List")',
        required: true,
        validator: (value: string) => {
          if (!value || typeof value !== 'string') {
            return 'Name is required';
          }
          if (value.trim().length < 3) {
            return 'Name must be at least 3 characters';
          }
          return null;
        }
      },
      {
        name: 'description',
        type: 'string',
        description: 'Brief description of what the app does',
        required: false
      },
      {
        name: 'icon',
        type: 'string',
        description: 'Phosphor icon name matching the app\'s purpose (e.g. "Calculator", "ChartBar", "Heart", "ShoppingCart"). Defaults to template icon if not specified.',
        required: false
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Optional folder (group_*) to place the project in. Leave empty for root level.',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          name: 'Calculator App',
          description: 'A calculator with basic operations',
          icon: 'Calculator'
        },
        expectedOutput: createSuccessResult('Project created', {}),
        description: 'Create calculator project',
        context: 'User wants to build a calculator'
      },
      {
        input: {
          name: 'Todo List',
          description: 'Task management app',
          icon: 'CheckSquare'
        },
        expectedOutput: createSuccessResult('Project created', {}),
        description: 'Create todo app project',
        context: 'User needs a todo list'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 1000,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { name, template = 'react-spa', description = '', icon, folderId } = params;
    
    log.info('[app_builder_create] Creating project:', { name, template, description });
    
    try {
      const templateConfig = PROJECT_TEMPLATES[template];
      if (!templateConfig) {
        return createErrorResult(
          'Invalid template',
          `Unknown template: ${template}. Valid templates: ${Object.keys(PROJECT_TEMPLATES).join(', ')}`,
          'validation'
        );
      }
      
      log.info('[app_builder_create] Using template:', templateConfig.name);
      
      const ydoc = new Y.Doc();
      const filesMap = ydoc.getMap('files');
      
      for (const [path, content] of Object.entries(templateConfig.files)) {
        const ytext = new Y.Text();
        ytext.insert(0, content);
        filesMap.set(path, ytext);
      }
      
      const ystate = Y.encodeStateAsUpdate(ydoc);
      ydoc.destroy();
      
      let binary = '';
      const bytes = new Uint8Array(ystate);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);
      
      log.info('[app_builder_create] Y.js document created with', Object.keys(templateConfig.files).length, 'files');
      
      const projectEntity = await entityService.create({
        entityType: 'app_project',
        name: name,
        icon: icon ?? templateConfig.icon,
        spaceId: context.spaceId,
        ...(folderId ? { folderId } : {}),
        skeleton: {
          '@type': 'app_project',
          version: '2',
          architecture: 'yjs',
          base64: base64,
          description: description,
          projectKind: templateConfig.projectKind,
          template: template,
          capabilities: {
            hasNextConfig: !!templateConfig.files['next.config.js'],
            hasVercelJson: false,
            hasApiRoutes: Object.keys(templateConfig.files).some(f => f.includes('/api/')),
            hasAppDirectory: Object.keys(templateConfig.files).some(f => f.startsWith('app/')),
            hasPagesDirectory: Object.keys(templateConfig.files).some(f => f.startsWith('pages/')),
            hasPublicFolder: Object.keys(templateConfig.files).some(f => f.startsWith('public/')),
            isShop: template.includes('shop'),
            detectedAt: new Date().toISOString(),
          },
          buildConfig: {
            framework: templateConfig.projectKind === 'nextjs' ? 'nextjs' : 'react',
            buildCommand: templateConfig.projectKind === 'nextjs' ? 'npm run build' : undefined,
            outputDirectory: templateConfig.projectKind === 'nextjs' ? '.next' : undefined,
          },
          createdBy: 'app_builder_create',
          createdAt: new Date().toISOString()
        }
      });
      
      log.info('[app_builder_create] app_project entity created:', projectEntity._id);
      
      const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
      const { executeCommand } = useCommandRegistryStore.getState();
      
      const windowResult = (await executeCommand('space_open_window', {
        entityId: projectEntity._id
      })) as StandardToolResult;
      
      const windowId = windowResult.data?.windowId;
      
      log.info('[app_builder_create] Project opened in AppBuilderApp:', windowId);

      // Inject build guidance into the active conversation so the AI has full context immediately.
      try {
        const guidance = REACT_SPA_PROMPT
          .replace(/\{\{projectName\}\}/g, name)
          .replace(/\{\{description\}\}/g, description)
          .replace(/\{\{targetEntityId\}\}/g, projectEntity._id);

        let threadId: string | undefined;
        try {
          const { getChatStore } = await import('../../../components/Space/SpaceChat/store/chatStore');
          const chatState = getChatStore(context.userId).getState();
          const activeTab = chatState.tabs.find((t: { id: string; threadId?: string }) => t.id === chatState.activeTabId);
          threadId = activeTab?.threadId || chatState.currentThreadIds?.['chat'];
        } catch (err) {
          log.warn('[app_builder_create] Could not get thread ID:', err);
        }

        if (threadId) {
          const { conversationStore } = await import('../../../components/Space/SpaceChat/store/conversationStore');
          const messageId = `app_guidance_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          conversationStore.addMessage(
            threadId,
            {
              id: messageId,
              role: 'user',
              threadId,
              content: [{ type: 'text', text: `[APP BUILD GUIDANCE]\n\n${guidance}` }],
              timestamp: new Date().toISOString(),
              metadata: { isTemplateGuidance: true, templateId: 'react-spa' } as any,
            },
            true // skipPersist — ephemeral, lives in memory for this session only
          );
          log.info('[app_builder_create] Build guidance injected into thread:', threadId);
        } else {
          log.warn('[app_builder_create] No active thread — guidance not injected');
        }
      } catch (err: any) {
        log.warn('[app_builder_create] Failed to inject guidance:', err);
      }

      const duration = Date.now() - startTime;
      
      return createSuccessResult(
        `Created "${name}" project (${templateConfig.name})!`,
        {
          entityId: projectEntity._id,
          windowId: windowId,
          name: name,
          template: template,
          projectKind: templateConfig.projectKind,
          description: description,
          entityType: 'app_project',
          fileCount: Object.keys(templateConfig.files).length,
          files: Object.keys(templateConfig.files),
          nextSteps: [
            'Use code_write to create components',
            'Use code_write to add functionality',
            'Click "Run" in AppBuilderApp to preview'
          ]
        },
        {
          metadata: {
            executionTime: duration
          },
          ui: {
            autoFocus: windowId,
            refreshContext: true
          }
        }
      );
    } catch (error: any) {
      log.error('[app_builder_create] Error:', error);
      return createErrorResult(
        'Failed to create app project',
        error.message || 'Unknown error',
        'execution'
      );
    }
  }
};
